import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Sliders, Sparkles, RotateCcw, Play, Plus, Trash2, Loader2, ChevronDown, ChevronRight, Bookmark, Save, Flame } from "lucide-react";

const BANDS = [
  ["B2", "Blue"], ["B3", "Green"], ["B4", "Red"], ["B8", "NIR"], ["B11", "SWIR-1"], ["B12", "SWIR-2"],
];

export default function BandSelectionPanel({ projectId, commodity, onApplied }) {
  const [cfg, setCfg] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const [showAdv, setShowAdv] = useState(false);
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState("");
  const [shareNew, setShareNew] = useState(false);
  const [savingPreset, setSavingPreset] = useState(false);

  const sortPresets = (arr) => [...(arr || [])].sort((a, b) => {
    const s = (b.shared ? 1 : 0) - (a.shared ? 1 : 0);
    return s || ((b.uses || 0) - (a.uses || 0));
  });
  const load = async (reset = false) => {
    try {
      const { data } = await api.get(`/projects/${projectId}/band-config${reset ? "?reset=1" : ""}`);
      setCfg(data);
    } catch (e) {
      /* silent */
    }
  };
  const loadPresets = async () => {
    try {
      const { data } = await api.get(`/me/band-presets`, { params: commodity ? { commodity } : {} });
      setPresets(sortPresets(data || []));
    } catch (e) {
      /* silent */
    }
  };
  useEffect(() => { load(); loadPresets(); /* eslint-disable-next-line */ }, [projectId, commodity]);

  const savePreset = async () => {
    const name = presetName.trim();
    if (!name) { toast.error("Enter a preset name"); return; }
    setSavingPreset(true);
    try {
      const { data } = await api.post(`/me/band-presets`, { name, commodity: commodity || "", config: cfg, shared: shareNew });
      setPresets(sortPresets((data || []).filter((p) => (p.commodity || "") === (commodity || ""))));
      setPresetName("");
      toast.success(shareNew ? `Preset "${name}" saved & shared with team` : `Preset "${name}" saved`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSavingPreset(false);
    }
  };
  const applyPreset = (p) => {
    if (!p || !p.config) return;
    setCfg((c) => ({ ...c, ...p.config, auto: false }));
    api.post(`/me/band-presets/${p.id}/use`).then(() => loadPresets()).catch(() => {});
    toast.success(`Preset "${p.name}" loaded - review & click Apply`);
  };
  const deletePreset = async (p) => {
    try {
      const { data } = await api.delete(`/me/band-presets/${p.id}`);
      setPresets(sortPresets((data || []).filter((x) => (x.commodity || "") === (commodity || ""))));
      toast.info(`Preset "${p.name}" removed`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!cfg) return null;
  const idx = cfg.indices || {};
  const hasCustom = (cfg.custom_ratios || []).some((c) => c.enabled);

  const setIndex = (k, patch) =>
    setCfg((c) => ({ ...c, auto: false, indices: { ...c.indices, [k]: { ...c.indices[k], ...patch } } }));
  const setCustom = (i, patch) =>
    setCfg((c) => ({ ...c, custom_ratios: c.custom_ratios.map((r, j) => (j === i ? { ...r, ...patch } : r)) }));
  const addCustom = () =>
    setCfg((c) => ({ ...c, custom_ratios: [...(c.custom_ratios || []), { name: "B11/B12", num: "B11", den: "B12", weight: 10, enabled: true }] }));
  const removeCustom = (i) =>
    setCfg((c) => ({ ...c, custom_ratios: c.custom_ratios.filter((_, j) => j !== i) }));

  const askAi = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/band-config/ai-detect`);
      setCfg((c) => {
        const indices = { ...c.indices };
        Object.entries(data.weights || {}).forEach(([k, w]) => {
          if (indices[k]) indices[k] = { ...indices[k], weight: Math.round(w), enabled: w > 0 };
        });
        return { ...c, indices, crosta: !!data.crosta, ai_note: data.note || "", auto: false };
      });
      toast.success("AI band recommendation applied - review & click Apply");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setAiBusy(false);
    }
  };

  const apply = async () => {
    setApplying(true);
    try {
      await api.put(`/projects/${projectId}/band-config`, { band_config: cfg });
      const { data } = await api.post(`/projects/${projectId}/band-config/apply`);
      toast.success(
        data.mode === "reprocess"
          ? "Custom ratios saved - re-processing imagery. Maps refresh in ~1–2 min."
          : "Band selection applied - recomputing maps from stored data (~25s)."
      );
      onApplied && onApplied();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="band-selection-panel">
      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
        <div className="flex items-center gap-2">
          <Sliders className="text-orange-500" size={18} />
          <h3 className="text-base font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            Band Selection & Mineral Indices
          </h3>
          {commodity && <span className="text-xs text-slate-500">· {commodity}</span>}
          {cfg.auto && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">AUTO</span>}
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" size="sm" variant="outline" className="border-slate-300 text-slate-600" onClick={() => load(true)} data-testid="band-reset-btn">
            <RotateCcw size={13} className="mr-1.5" /> Profile
          </Button>
          <Button type="button" size="sm" variant="outline" className="border-indigo-300 text-indigo-600 hover:bg-indigo-50" onClick={askAi} disabled={aiBusy} data-testid="band-ai-btn">
            {aiBusy ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Sparkles size={13} className="mr-1.5" />} Ask AI
          </Button>
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Choose which Sentinel-2 spectral indices drive the prospectivity model and how strongly they are weighted.
      </p>

      <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="band-presets">
        <div className="flex items-center gap-2 mb-2">
          <Bookmark size={14} className="text-orange-500" />
          <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">Band Presets Library</span>
          <span className="text-[10px] text-slate-400">· {commodity || "General"}</span>
        </div>
        {presets.length > 0 ? (
          <div className="flex flex-wrap gap-1.5 mb-2.5" data-testid="band-presets-list">
            {presets.map((pr) => {
              const isTop = pr.shared && (pr.uses || 0) > 0 && pr.id === presets.find((x) => x.shared && (x.uses || 0) > 0)?.id;
              return (
              <span key={pr.id} className={`inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded-full border text-xs ${isTop ? "bg-orange-100 border-orange-400 text-orange-900 ring-1 ring-orange-300" : pr.shared ? "bg-orange-50 border-orange-200 text-orange-800" : "bg-white border-slate-300 text-slate-700"}`} data-testid={`band-preset-${pr.id}`} title={isTop ? `Most-used team recipe (${pr.uses} uses)` : pr.shared ? `Shared by ${pr.owner_name || "team"}` : "Personal preset"}>
                {isTop && <Flame size={11} className="text-orange-500" data-testid={`band-preset-top-${pr.id}`} />}
                {pr.shared && !isTop && <span className="text-[8px] font-bold px-1 rounded bg-orange-500 text-white leading-none py-0.5" data-testid={`band-preset-team-${pr.id}`}>TEAM</span>}
                <button type="button" className="font-medium hover:text-orange-600" onClick={() => applyPreset(pr)} data-testid={`band-preset-apply-${pr.id}`}>{pr.name}</button>
                {pr.mine && (
                  <button type="button" className="text-slate-400 hover:text-red-500" onClick={() => deletePreset(pr)} data-testid={`band-preset-delete-${pr.id}`}><Trash2 size={11} /></button>
                )}
              </span>
              );
            })}
          </div>
        ) : (
          <p className="text-[11px] text-slate-400 mb-2.5" data-testid="band-presets-empty">No saved presets yet. Tune the weights below, then save this recipe to reuse it on any project.</p>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <Input value={presetName} onChange={(e) => setPresetName(e.target.value)} placeholder="Name this recipe (e.g. Epithermal Au)" className="h-8 text-xs flex-1 min-w-[150px]" data-testid="band-preset-name" />
          <label className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer select-none" title="Make this recipe available to your whole team">
            <input type="checkbox" className="h-3.5 w-3.5 accent-orange-500" checked={shareNew} onChange={(e) => setShareNew(e.target.checked)} data-testid="band-preset-share" />
            Share with team
          </label>
          <Button type="button" size="sm" variant="outline" className="border-orange-300 text-orange-600 hover:bg-orange-50 shrink-0" onClick={savePreset} disabled={savingPreset} data-testid="band-preset-save">
            {savingPreset ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Save size={13} className="mr-1.5" />} Save preset
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {Object.entries(idx).map(([k, v]) => (
          <div key={k} className="flex items-center gap-3" data-testid={`band-row-${k}`}>
            <input
              type="checkbox"
              className="h-4 w-4 accent-orange-500 shrink-0"
              checked={!!v.enabled}
              onChange={(e) => setIndex(k, { enabled: e.target.checked })}
              data-testid={`band-toggle-${k}`}
            />
            <div className="w-44 shrink-0">
              <div className="text-sm font-medium text-slate-800">{v.label}</div>
              <div className="text-[10px] text-slate-400 font-mono">{v.ratio}</div>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={v.weight}
              disabled={!v.enabled}
              onChange={(e) => setIndex(k, { weight: Number(e.target.value) })}
              className="flex-1 min-w-0 accent-orange-500 disabled:opacity-40"
              data-testid={`band-weight-${k}`}
            />
            <span className="w-10 text-right text-sm font-semibold text-slate-700 tabular-nums">{v.weight}</span>
          </div>
        ))}
      </div>

      <label className="mt-4 flex items-center gap-2.5 text-sm text-slate-700 cursor-pointer">
        <input
          type="checkbox"
          className="h-4 w-4 accent-orange-500"
          checked={!!cfg.crosta}
          onChange={(e) => setCfg((c) => ({ ...c, crosta: e.target.checked, auto: false }))}
          data-testid="band-crosta-toggle"
        />
        Include Crosta / feature-oriented PCA alteration enhancement (Map 08)
      </label>

      <button
        type="button"
        onClick={() => setShowAdv((s) => !s)}
        className="mt-4 flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800"
        data-testid="band-advanced-toggle"
      >
        {showAdv ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Advanced custom band ratios (triggers re-processing)
      </button>

      {showAdv && (
        <div className="mt-3 space-y-2 border-t border-slate-100 pt-3" data-testid="band-custom-list">
          {(cfg.custom_ratios || []).map((c, i) => (
            <div key={i} className="flex items-center gap-2 flex-wrap" data-testid={`band-custom-${i}`}>
              <input type="checkbox" className="h-4 w-4 accent-orange-500" checked={!!c.enabled} onChange={(e) => setCustom(i, { enabled: e.target.checked })} data-testid={`band-custom-${i}-enable`} />
              <select value={c.num} onChange={(e) => setCustom(i, { num: e.target.value })} className="h-8 text-xs border border-slate-300 rounded px-1.5" data-testid={`band-custom-${i}-num`}>
                {BANDS.map(([b, l]) => <option key={b} value={b} label={`${b} · ${l}`} />)}
              </select>
              <span className="text-slate-400 text-sm">/</span>
              <select value={c.den} onChange={(e) => setCustom(i, { den: e.target.value })} className="h-8 text-xs border border-slate-300 rounded px-1.5" data-testid={`band-custom-${i}-den`}>
                {BANDS.map(([b, l]) => <option key={b} value={b} label={`${b} · ${l}`} />)}
              </select>
              <input type="range" min="0" max="100" value={c.weight} onChange={(e) => setCustom(i, { weight: Number(e.target.value) })} className="flex-1 min-w-[80px] accent-orange-500" data-testid={`band-custom-${i}-weight`} />
              <span className="w-8 text-right text-xs font-semibold text-slate-700 tabular-nums">{c.weight}</span>
              <button type="button" onClick={() => removeCustom(i)} className="text-slate-400 hover:text-red-500" data-testid={`band-custom-${i}-remove`}><Trash2 size={14} /></button>
            </div>
          ))}
          <Button type="button" size="sm" variant="outline" className="border-slate-300 text-slate-600" onClick={addCustom} data-testid="band-add-custom">
            <Plus size={13} className="mr-1.5" /> Add custom ratio
          </Button>
        </div>
      )}

      {cfg.ai_note && (
        <p className="mt-4 text-xs text-slate-600 bg-indigo-50 border border-indigo-100 rounded-lg p-3" data-testid="band-ai-note">
          <Sparkles size={12} className="inline mr-1 text-indigo-500" /> {cfg.ai_note}
        </p>
      )}

      <div className="mt-5 flex items-center justify-end">
        <Button type="button" size="sm" className="bg-orange-500 hover:bg-orange-600 text-white" onClick={apply} disabled={applying} data-testid="band-apply-btn">
          {applying ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Play size={14} className="mr-1.5" />}
          {hasCustom ? "Apply & Re-process" : "Apply (fast recompute)"}
        </Button>
      </div>
    </div>
  );
}
